import { declOfNum } from '@/helpers/declOfNum'
import { getTariffValueMin } from '@pages/CoursesCatalog/modals/CourseEnrollmentModal/components/SelectTariffValue/SelectTariffValue'
import {
  getCrmLinkPriceNowFromBase,
  resolveCrmLinkDiscountMode,
  type CrmLinkDiscountMode,
} from '../../discountMode.utils'
import { isStreamCustomPeriodTariff } from '../../tariffTypes'

/** Только stream_custom_period (месячные периоды). Для старых импортов. */
export const isStreamCustomMonthsLinkTariff = (tariffType?: string | null) =>
  tariffType === 'stream_custom_period'

/** Совпадает с isStreamCustomPeriodTariff (period/days/weeks). */
export const isStreamCustomPeriodLinkTariff = (tariffType?: string | null) =>
  isStreamCustomPeriodTariff(tariffType ?? undefined)

const getStreamCustomPeriodValueLabel = (
  tariffType?: string | null,
  value?: number,
  periodUnit?: 'month' | 'day' | null,
) => {
  const periodValue = Math.max(0, Math.trunc(Number(value) || 0))

  if (periodUnit === 'day') {
    return `${declOfNum(periodValue, ['день', 'дня', 'дней'])}`
  }
  if (periodUnit === 'month') {
    return `${declOfNum(periodValue, ['месяц', 'месяца', 'месяцев'])}`
  }

  switch (tariffType) {
    case 'stream_custom_days':
      return `${declOfNum(periodValue, ['день', 'дня', 'дней'])}`
    case 'stream_custom_weeks':
      return `${declOfNum(periodValue, ['неделя', 'недели', 'недель'])}`

    default:
      return `${declOfNum(periodValue, ['месяц', 'месяца', 'месяцев'])}`
  }
}

/** База цены выбранного периода из таблицы тарифа. */
export const getStreamCustomPeriodBasePrice = (
  tariff: ICourseTariff | null | undefined,
  value: unknown,
) => {
  const periodValue = Number(value)
  if (!tariff || !Number.isFinite(periodValue) || periodValue < 1) return 0

  const periodPrice =
    tariff.type === 'stream_custom_days'
      ? tariff.tariffDays?.find((item) => Number(item.days) === periodValue)
          ?.price
      : tariff.type === 'stream_custom_weeks'
      ? tariff.tariffWeeks?.find((item) => Number(item.weeks) === periodValue)
          ?.price
      : tariff.tariffPeriods?.find((item) => Number(item.months) === periodValue)
          ?.price

  return Number(periodPrice) || Number(tariff?.first_price) || 0
}

export const getStreamCustomPeriodSuggestedPrice = (
  tariff: ICourseTariff | null | undefined,
  value: unknown,
  discount: unknown,
  discountMode?: CrmLinkDiscountMode | null,
  applyDiscountEachPayment?: boolean | null,
  isPeriodFixed?: boolean,
) => {
  const basePrice = getStreamCustomPeriodBasePrice(tariff, value)
  const mode = resolveCrmLinkDiscountMode({
    discount: Number(discount) || 0,
    discountMode,
    applyDiscountEachPayment,
    isPeriodFixed,
  })
  // Stream: оплата конкретного пакета из таблицы (2/5/10 дней и т.п.).
  // Скидку every_payment НЕ множим на value — ×N только у помесячного month.
  return getCrmLinkPriceNowFromBase(basePrice, discount, mode)
}

export const getStreamCustomPeriodHint = (
  value: unknown,
  basePrice: number,
  discount: unknown,
  price: number,
  tariffType?: string | null,
  periodUnit?: 'month' | 'day' | null,
) => {
  const periodValue = Math.max(0, Math.trunc(Number(value) || 0))
  const discountValue = Math.max(0, Number(discount) || 0)
  const resolvedBasePrice =
    basePrice > 0 ? basePrice : Math.max(0, Number(price) + discountValue)
  return `Период: ${periodValue} ${getStreamCustomPeriodValueLabel(
    tariffType,
    periodValue,
    periodUnit,
  )} — ${resolvedBasePrice} ₽. Скидка ${discountValue} ₽. К оплате ${price} ₽.`
}

export const STREAM_CUSTOM_PERIOD_PRICE_LOCKED_TOOLTIP =
  'Итоговая сумма считается автоматически: цена периода минус скидка (если режим не «только продление»). Редактирование вручную недоступно.'

export const STREAM_CUSTOM_PERIOD_UNFIXED_PRICE_HINT =
  'Период оплаты не зафиксирован: покупатель сам выберет длительность из таблицы ценообразования тарифа. К оплате будет цена выбранного периода с учётом режима скидки.'

/** period_unit по умолчанию при фиксации: days → day, иначе month (в т.ч. weeks/period). */
export const getDefaultStreamPeriodUnit = (
  tariffType?: string | null,
): 'month' | 'day' =>
  tariffType === 'stream_custom_days' ? 'day' : 'month'

export const getStreamPeriodToggleResetValues = (
  tariff: ICourseTariff | null | undefined,
  value: unknown,
) => ({
  value: Math.max(
    1,
    Math.trunc(
      Number(value) || (tariff ? getTariffValueMin(tariff) : 0) || 1,
    ),
  ),
  period_unit: getDefaultStreamPeriodUnit(tariff?.type),
})

/** Период для расчёта: выбранный value или мин. периода тарифа (если ссылка stream). */
export const resolveStreamPeriodValue = (
  value: number | null | undefined,
  tariff: ICourseTariff | null | undefined,
  isStreamLink: boolean,
) =>
  value ?? (isStreamLink && tariff ? getTariffValueMin(tariff) : null)

export const isStreamDiscountInvalid = (
  tariff: ICourseTariff | null | undefined,
  periodValue: unknown,
  discount: unknown,
) => {
  const basePrice = getStreamCustomPeriodBasePrice(tariff, periodValue)
  const discountValue = Number(discount) || 0
  return basePrice > 0 && discountValue > 0 && discountValue >= basePrice
}

/** Net основного курса в «Итого пакета». */
export const getStreamPackageMainNet = (
  priceWatch: unknown,
  suggestedPrice: number,
) => {
  // null/'' не считаем валидной ценой — иначе Number(null)===0 и UI залипает на 0.
  if (priceWatch === null || priceWatch === undefined || priceWatch === '') {
    return suggestedPrice
  }
  const mainPrice = Number(priceWatch)
  return Number.isFinite(mainPrice) ? Math.max(0, mainPrice) : suggestedPrice
}

/** price основной ссылки на submit — всегда net при stream (value = пресет или фиксация). */
export const resolveStreamMainSubmitPrice = (
  isStreamLink: boolean,
  price: unknown,
) => (isStreamLink ? Math.max(0, Number(price) || 0) : null)

/** Фиксация stream-ссылки: как у month — по period_unit. */
export const isStreamPeriodFixedByUnit = (periodUnit?: string | null) =>
  periodUnit === 'month' || periodUnit === 'day'

/** Маппинг доп. курса stream на payload ссылки (period_unit как у month). */
export const mapStreamAdditionalCourseForSubmit = <T extends object>(
  item: T & {
    value?: number | null
    price?: number | null
    discount?: number | null
    discount_mode?: CrmLinkDiscountMode | null
    apply_discount_each_payment?: boolean | null
    period_unit?: 'month' | 'day' | null
    is_period_fixed?: boolean | null
  },
  tariff: ICourseTariff,
): T & {
  value: number
  period_unit: 'month' | 'day' | null
  is_period_fixed: boolean
  apply_discount_each_payment: boolean
  discount: number | null
  discount_mode: CrmLinkDiscountMode | null
  price: number
} => {
  const nextValue = Math.max(
    1,
    Math.trunc(Number(item.value) || getTariffValueMin(tariff) || 1),
  )
  const discount = Number(item.discount) || 0
  const isPeriodFixed = Boolean(item.is_period_fixed)
  const mode =
    discount > 0
      ? resolveCrmLinkDiscountMode({
          discount,
          discountMode: item.discount_mode,
          applyDiscountEachPayment: item.apply_discount_each_payment,
          isPeriodFixed,
        })
      : null
  const netPrice =
    item.price != null && item.price !== undefined
      ? Math.max(0, Number(item.price) || 0)
      : getStreamCustomPeriodSuggestedPrice(
          tariff,
          nextValue,
          discount,
          mode,
          item.apply_discount_each_payment,
          isPeriodFixed,
        )

  if (isPeriodFixed) {
    const unit =
      item.period_unit === 'day' || item.period_unit === 'month'
        ? item.period_unit
        : getDefaultStreamPeriodUnit(tariff.type)

    return {
      ...item,
      value: nextValue,
      period_unit: unit,
      is_period_fixed: true,
      discount: discount > 0 ? discount : null,
      discount_mode: mode,
      apply_discount_each_payment: mode === 'every_payment',
      price: netPrice,
    }
  }

  return {
    ...item,
    value: nextValue,
    period_unit: null,
    is_period_fixed: false,
    discount: discount > 0 ? discount : null,
    discount_mode: mode,
    apply_discount_each_payment: mode === 'every_payment',
    price: netPrice,
  }
}

export const isStreamAdditionalCourseDiscountInvalid = (
  item: {
    value?: number | null
    discount?: number | null
    price?: number | null
  },
  tariff: ICourseTariff | null | undefined,
) => {
  if (!tariff || !isStreamCustomPeriodTariff(tariff.type)) return false

  const discount = Number(item.discount) || 0
  if (discount <= 0) return false

  const resolveComparableBase = () => {
    if (item.value != null) {
      const tableBase = getStreamCustomPeriodBasePrice(tariff, item.value)
      if (tableBase > 0) return tableBase
    } else {
      const minBase = getStreamCustomPeriodBasePrice(
        tariff,
        getTariffValueMin(tariff),
      )
      if (minBase > 0) return minBase
    }

    // Фиксация периода: value может быть «2 месяца» при тарифе weeks/days —
    // строки в таблице нет (base=0). Сравниваем с введённым итого пакета.
    const packagePrice = Number(item.price)
    return Number.isFinite(packagePrice) && packagePrice > 0 ? packagePrice : 0
  }

  const base = resolveComparableBase()
  return base > 0 && discount >= base
}
